import type { OrderModel } from 'src/types/order';
import type { MediaModel } from 'src/types/media';
import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useRef, useEffect } from 'react';

import { PaymentMethodEnum } from '../types/order-form.types';

import type { BillingSchemaType, NewOrderSchemaType } from '../types/order-form.types';

interface UseOrderPreloadParams {
  currentOrder: OrderModel | undefined;
  isEditMode: boolean;
  methods: UseFormReturn<NewOrderSchemaType>;
  billingMethods: UseFormReturn<BillingSchemaType>;
  medias: MediaModel[];
  setBillingMethod: (method: 'email' | 'platform') => void;
  setPaymentMethod: (method: PaymentMethodEnum) => void;
  setSearchMedia: (search: string) => void;
}

export function useOrderPreload({
  currentOrder,
  isEditMode,
  methods,
  billingMethods,
  medias,
  setBillingMethod,
  setPaymentMethod,
  setSearchMedia,
}: UseOrderPreloadParams) {
  const hasLoadedBasicData = useRef(false);
  const hasLoadedMediaData = useRef(false);

  const { setValue } = methods;

  // Phase 1: Load basic fields (no dependency on medias)
  useEffect(() => {
    if (!isEditMode || !currentOrder || hasLoadedBasicData.current) return;

    // Media search trigger
    if (currentOrder.normalDetails?.media) {
      setSearchMedia(currentOrder.normalDetails.media.name);
      setValue('mediaId', {
        value: currentOrder.normalDetails.media.id,
        label: currentOrder.normalDetails.media.name,
      });
    } else if (currentOrder.normalDetails?.mediaId) {
      const media = medias?.find((m) => m.id === currentOrder.normalDetails?.mediaId);
      if (media) {
        setSearchMedia(media.name);
        setValue('mediaId', { value: media.id, label: media.name });
      }
    }

    // Price, currency, dates
    setValue('price', currentOrder.price || 0);
    setValue('currency', currentOrder.currency ?? 0);

    if (currentOrder.limitDate) {
      setValue('limitDate', dayjs(currentOrder.limitDate));
    }

    // Category commission
    if (currentOrder.normalDetails?.extraSensibleCategory) {
      setValue('categoryCommission', currentOrder.normalDetails.extraSensibleCategory);
    }

    // URLs
    if (currentOrder.finalUrl) setValue('finalUrl', currentOrder.finalUrl);

    // Cost fields
    if (currentOrder.costPrice !== undefined) setValue('costPrice', currentOrder.costPrice);
    if (currentOrder.costPriceCurrency !== undefined && currentOrder.costPriceCurrency !== null) {
      setValue('costPriceCurrency', currentOrder.costPriceCurrency);
    } else {
      setValue('costPriceCurrency', 0);
    }

    // Discount and special fields
    if (currentOrder.discountPercentage !== undefined)
      setValue('discountPercentage', currentOrder.discountPercentage);
    if (currentOrder.internalPlatformId)
      setValue('internalPlatformId', currentOrder.internalPlatformId);
    if (currentOrder.redactionUrl) setValue('redactionUrl', currentOrder.redactionUrl);

    // Redaction
    setValue('requireRedaction', currentOrder.normalDetails?.requireRedaction || false);
    if (currentOrder.normalDetails?.wordsWrite)
      setValue('wordCount', currentOrder.normalDetails.wordsWrite);

    // Extra details
    if (currentOrder.normalDetails?.orderDetail)
      setValue('orderDetail', currentOrder.normalDetails.orderDetail);
    if (currentOrder.normalDetails?.extraH1) setValue('title', currentOrder.normalDetails.extraH1);
    if (currentOrder.normalDetails?.extraTermsToAvoid)
      setValue('extraTermsToAvoid', currentOrder.normalDetails.extraTermsToAvoid);
    if (currentOrder.normalDetails?.extraHtml)
      setValue('extraHtml', currentOrder.normalDetails.extraHtml);
    if (currentOrder.normalDetails?.extraSource)
      setValue('extraSource', currentOrder.normalDetails.extraSource);

    // Links and keywords
    const nd = currentOrder.normalDetails;
    if (nd?.url1) setValue('url1', nd.url1);
    if (nd?.keyword1) setValue('keyword1', nd.keyword1);
    if (nd?.linkType1 !== undefined) setValue('linkType1', nd.linkType1);
    if (nd?.url2) setValue('url2', nd.url2);
    if (nd?.keyword2) setValue('keyword2', nd.keyword2);
    if (nd?.linkType2 !== undefined) setValue('linkType2', nd.linkType2);
    if (nd?.url3) setValue('url3', nd.url3);
    if (nd?.keyword3) setValue('keyword3', nd.keyword3);
    if (nd?.linkType3 !== undefined) setValue('linkType3', nd.linkType3);
    if (nd?.url4) setValue('url4', nd.url4);
    if (nd?.keyword4) setValue('keyword4', nd.keyword4);
    if (nd?.linkType4 !== undefined) setValue('linkType4', nd.linkType4);

    // Billing method
    if (currentOrder.platformId && currentOrder.platform) {
      setBillingMethod('platform');
      billingMethods.setValue('platformId', {
        value: currentOrder.platformId,
        label: currentOrder.platform,
      } as any);
    } else {
      setBillingMethod('email');
      if (currentOrder.invoice) {
        billingMethods.setValue('customerId', currentOrder.invoice.id || undefined);
        billingMethods.setValue('firstName', currentOrder.invoice.name || '');
        billingMethods.setValue('lastName', currentOrder.invoice.surname || '');
        billingMethods.setValue('company', currentOrder.invoice.company || '');
        billingMethods.setValue(
          'email',
          currentOrder.normalDetails?.externalEmail || currentOrder.user?.email || ''
        );
        billingMethods.setValue('phone', currentOrder.invoice.phone || '');
        billingMethods.setValue('nif', currentOrder.invoice.nif || '');
        billingMethods.setValue('country', currentOrder.invoice.country || '');
        billingMethods.setValue('city', currentOrder.invoice.city || '');
        billingMethods.setValue('postalCode', currentOrder.invoice.postalCode || '');
        billingMethods.setValue('address', currentOrder.invoice.address || '');
      }
    }

    // Payment method
    if (currentOrder.paymentMethod !== undefined) {
      if (currentOrder.paymentMethod === 2) {
        setPaymentMethod(PaymentMethodEnum.PayPal);
      } else if (currentOrder.paymentMethod === 3) {
        setPaymentMethod(PaymentMethodEnum.WiseTransfer);
      } else {
        setPaymentMethod(PaymentMethodEnum.Pending);
      }
    }

    hasLoadedBasicData.current = true;
  }, [
    isEditMode,
    currentOrder,
    billingMethods,
    setValue,
    setBillingMethod,
    setPaymentMethod,
    setSearchMedia,
    medias,
  ]);

  // Phase 2: Load media-dependent data when medias are available
  useEffect(() => {
    if (
      !isEditMode ||
      !currentOrder ||
      !medias?.length ||
      !hasLoadedBasicData.current ||
      hasLoadedMediaData.current
    )
      return;

    if (currentOrder.normalDetails?.mediaId) {
      const fullMediaObject = medias.find((m) => m.id === currentOrder.normalDetails?.mediaId);

      if (fullMediaObject) {
        setValue('mediaId', {
          value: fullMediaObject.id,
          label: fullMediaObject.name,
        });

        // Load category after media is ready
        if (currentOrder.normalDetails?.categoryId) {
          setTimeout(() => {
            setValue('categoryId', currentOrder.normalDetails!.categoryId!, {
              shouldValidate: false,
              shouldDirty: false,
              shouldTouch: false,
            });
          }, 500);
        }

        // Re-set currency values after media auto-fill to preserve original values
        setTimeout(() => {
          setValue('currency', currentOrder.currency ?? 0);
          if (
            currentOrder.costPriceCurrency !== undefined &&
            currentOrder.costPriceCurrency !== null
          ) {
            setValue('costPriceCurrency', currentOrder.costPriceCurrency);
          }
        }, 600);
      }
    }

    hasLoadedMediaData.current = true;
  }, [isEditMode, currentOrder, medias, setValue]);
}
